import React, { useState, useEffect } from 'react';
import BackofficeLayout from '../../components/backoffice/BackofficeLayout';
import DataTable from '../../components/backoffice/DataTable';
import ConfirmDialog from '../../components/backoffice/ConfirmDialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { testimonialService } from '../../services/testimonialService';
import { Star, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return format(date, 'dd MMM yyyy', { locale: fr });
  } catch {
    return '-';
  }
};

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const data = await testimonialService.getAllTestimonials();
      setTestimonials(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast.error('Erreur lors du chargement des témoignages');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await testimonialService.updateTestimonial(id, { approuve: true });
      toast.success('Témoignage approuvé');
      loadTestimonials();
    } catch (error) {
      console.error('Error approving testimonial:', error);
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleReject = async (id) => {
    try {
      await testimonialService.updateTestimonial(id, { approuve: false });
      toast.success('Témoignage rejeté');
      loadTestimonials();
    } catch (error) {
      console.error('Error rejecting testimonial:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  const handleDelete = async () => {
    try {
      await testimonialService.deleteTestimonial(deleteDialog.id);
      toast.success('Témoignage supprimé');
      setDeleteDialog({ open: false, id: null });
      loadTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredTestimonials = testimonials.filter((t) => {
    if (filter === 'all') return true;
    if (filter === 'approved') return t.approuve;
    if (filter === 'pending') return !t.approuve;
    return true;
  });

  const columns = [
    {
      header: 'Client',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <span className="text-amber-700 font-semibold">
              {row.nomClient?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <span className="font-medium">{row.nomClient}</span>
        </div>
      ),
    },
    {
      header: 'Note',
      accessorKey: 'note',
      cell: (row) => (
        <div className="flex">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-4 h-4 ${
                star <= row.note
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-slate-300'
              }`}
            />
          ))}
        </div>
      ),
    },
    {
      header: 'Commentaire',
      accessorKey: 'commentaire',
      cell: (row) => (
        <p className="text-sm text-slate-600 line-clamp-2 max-w-xs">
          {row.commentaire}
        </p>
      ),
    },
    {
      header: 'Statut',
      accessorKey: 'approuve',
      cell: (row) => (
        <Badge
          className={
            row.approuve
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }
        >
          {row.approuve ? 'Approuvé' : 'En attente'}
        </Badge>
      ),
    },
    {
      header: 'Date',
      accessorKey: 'dateCreation',
      cell: (row) => (
        <span className="text-sm text-slate-600">
          {formatDate(row.dateCreation)}
        </span>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1">
          {!row.approuve ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleApprove(row.id)}
              className="text-green-600 hover:text-green-700"
              title="Approuver"
            >
              <CheckCircle className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleReject(row.id)}
              className="text-yellow-600 hover:text-yellow-700"
              title="Rejeter"
            >
              <XCircle className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, id: row.id })}
            className="text-red-600 hover:text-red-700"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
      className: 'text-right',
    },
  ];

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Gestion des Témoignages
          </h1>
          <p className="text-slate-600 mt-1">
            {testimonials.length} témoignage{testimonials.length > 1 ? 's' : ''}{' '}
            au total
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtrer par statut</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les témoignages</SelectItem>
                <SelectItem value="approved">Approuvés</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Table */}
        <DataTable
          columns={columns}
          data={filteredTestimonials}
          loading={loading}
          emptyMessage="Aucun témoignage trouvé"
        />
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
        title="Supprimer le témoignage"
        description="Êtes-vous sûr de vouloir supprimer ce témoignage ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        onConfirm={handleDelete}
        variant="destructive"
      />
    </BackofficeLayout>
  );
};

export default Testimonials;
